import { PageHeader } from '@/components/layout/PageHeader';
import { PageShell } from '@/components/layout/PageShell';
import { Button } from '@/components/ui/button';
import { ApiError, api } from '@/lib/api';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Pencil } from 'lucide-react';

const SOURCE_LABEL: Record<string, string> = {
  manual: '수동 편집',
  restore_default: '기본값 복원',
  create: '최초 생성',
};

export function AdminSystemPromptDetail({ promptKey }: { promptKey: string }) {
  const navigate = useNavigate();
  const detailQ = useQuery({
    queryKey: ['admin', 'system-prompts', promptKey],
    queryFn: () => api.adminSystemPromptGet(promptKey),
  });
  const versionsQ = useQuery({
    queryKey: ['admin', 'system-prompts', promptKey, 'versions'],
    queryFn: () => api.adminSystemPromptVersionsList(promptKey),
  });

  const detail = detailQ.data;

  return (
    <PageShell
      header={
        <PageHeader
          title={detail?.label ?? '시스템 프롬프트'}
          description={detail?.description}
          actions={
            <div className="flex items-center gap-2">
              <Button variant="secondary" onClick={() => navigate({ to: '/admin/system-prompts' })}>
                <ArrowLeft className="size-4" />
                목록으로
              </Button>
              {detail ? (
                <Button
                  onClick={() =>
                    navigate({
                      to: '/admin/system-prompts/$key/edit',
                      params: { key: detail.key },
                    })
                  }
                >
                  <Pencil className="size-4" />
                  편집
                </Button>
              ) : null}
            </div>
          }
        />
      }
    >
      {detailQ.isLoading ? (
        <p className="text-sm text-foreground-muted">불러오는 중…</p>
      ) : detailQ.error ? (
        <p className="text-sm text-destructive">
          {detailQ.error instanceof ApiError
            ? detailQ.error.message
            : '상세를 불러오지 못했습니다.'}
        </p>
      ) : detail ? (
        <div className="space-y-6">
          <section className="space-y-1.5">
            <div className="flex items-center gap-2">
              <span className="text-xs uppercase tracking-wider text-foreground-muted">key</span>
              <code className="font-mono text-sm">{detail.key}</code>
              <span
                className={`ml-2 inline-flex items-center px-2 h-5 rounded-sm text-[10px] uppercase tracking-wider ${
                  detail.isDefault
                    ? 'bg-surface-muted text-foreground-muted'
                    : 'bg-success/15 text-success'
                }`}
              >
                {detail.isDefault ? '기본값' : '변경됨'}
              </span>
            </div>
            {detail.hint ? <p className="text-sm text-foreground-muted">{detail.hint}</p> : null}
          </section>

          <section>
            <h3 className="text-sm font-medium mb-2">현재 값 ({detail.currentValue.length}자)</h3>
            <pre className="rounded-md border border-border bg-surface-muted/30 px-3 py-2 text-sm whitespace-pre-wrap font-mono leading-relaxed">
              {detail.currentValue || '(빈 값 — 가이드 비활성화)'}
            </pre>
          </section>

          {!detail.isDefault ? (
            <section>
              <h3 className="text-sm font-medium mb-2">
                코드 기본값 ({detail.defaultValue.length}자)
              </h3>
              <pre className="rounded-md border border-border bg-surface-muted/10 px-3 py-2 text-xs whitespace-pre-wrap font-mono leading-relaxed text-foreground-muted">
                {detail.defaultValue}
              </pre>
            </section>
          ) : null}

          <section>
            <h3 className="text-sm font-medium mb-2">변경 이력</h3>
            {versionsQ.isLoading ? (
              <p className="text-sm text-foreground-muted">불러오는 중…</p>
            ) : versionsQ.data && versionsQ.data.versions.length > 0 ? (
              <div className="rounded-2xl border border-border bg-surface overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-surface-muted/40 text-xs uppercase tracking-wider text-foreground-muted">
                    <tr>
                      <th className="px-4 py-2.5 font-medium text-left">버전</th>
                      <th className="px-4 py-2.5 font-medium text-left">유형</th>
                      <th className="px-4 py-2.5 font-medium text-left">변경자</th>
                      <th className="px-4 py-2.5 font-medium text-left">메모</th>
                      <th className="px-4 py-2.5 font-medium text-left">시각</th>
                    </tr>
                  </thead>
                  <tbody>
                    {versionsQ.data.versions.map((v) => (
                      <tr key={v.id} className="border-t border-border">
                        <td className="px-4 py-2 font-mono text-xs">#{v.version}</td>
                        <td className="px-4 py-2 text-xs">{SOURCE_LABEL[v.source] ?? v.source}</td>
                        <td className="px-4 py-2 text-xs">
                          {v.createdBy
                            ? (v.createdBy.displayName ?? v.createdBy.email ?? '?')
                            : '—'}
                        </td>
                        <td className="px-4 py-2 text-xs text-foreground-muted">
                          {v.changelog ?? '—'}
                        </td>
                        <td className="px-4 py-2 text-xs text-foreground-muted">
                          {new Date(v.createdAt).toLocaleString('ko-KR')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-foreground-muted">변경 이력이 없습니다.</p>
            )}
          </section>
        </div>
      ) : null}
    </PageShell>
  );
}
